<template>
  <div class="system-campaigns">

    <Search @filter-campaigns="applyFilters" :tag="tag"/>

    <div class="mb-6 campaigns-owner" v-if="account">
      <div v-if="rowsCount === 0">
        <div v-if="!showDeleteBtn">
          <span>{{ account.name }}</span>
        </div>
        <div v-else>
          <span class="mr-5">{{ account.name }}</span>
          <a href="#" @click.prevent="deleteAccount(account.id)" class="delete-account">Delete Account</a>
        </div>
      </div>
      <div v-else>
        <span>{{ `${account.name}'s Campaigns` }}</span>
      </div>
    </div>

    <a-g-speakol-table
      ref="systemCampaignsTable"
      :filters="agFilters"
      :columns="agColumns"
      :options="agOptions">
    </a-g-speakol-table>

    <SpeakolNotificationToaster :show="toaster.show" :message="toaster.message" :icon="toaster.icon"
                                :closeButtonText="toaster.closeButtonText" @close="toaster.show=false">
      <template v-slot:close>
        <vs-icon class="x-icon" color="#FFFFFF" icon="speakol-cancel" icon-pack="speakol"></vs-icon>
      </template>
    </SpeakolNotificationToaster>

    <delete-account ref="delete-account" :account="account"></delete-account>
  </div>
</template>

<script>
import SystemCampaignsDataSource from '@/datasources/SystemCampaignsDataSource'

import AGSpeakolTable from '@/components/AGSpeakolTable'

import CampaignActionsDropdown from '@/views/campaigns/index/components/CampaignActionsDropdown.vue'

import CampaignStatus from './components/CampaignStatus'

import Spent from './components/Spent'

import Search from './components/Search'

import CampaignActionsMenu from './components/CampaignActionsMenu'

import CampaignColumn from './components/campaign-col'
import CampaignNameHeader from './components/CampaignNameHeader'
import SpeakolNotificationToaster from '@/components/SpeakolNotificationToaster'
import {trim} from '@/utils/StringOperations.js'
import DeleteAccount from '@/views/accounts/components/delete-account.vue'

import CreatedAtColumn from '@/views/campaigns/index/components/AgTableComponents/CreatedAt.vue'
import { getDateAndTimeFromDateTimeString } from '@/filters/Dates'

export default {
  components: {
    Search,
    Spent,
    AGSpeakolTable,
    CampaignStatus,
    CampaignActionsDropdown,
    CampaignActionsMenu,
    CampaignColumn,
    CampaignNameHeader,
    SpeakolNotificationToaster,
    DeleteAccount,
    CreatedAtColumn
  },
  props: ['tag'],
  data () {
    return {
      agOptions: this.getTableOptions(),
      agColumns: this.getTableColumns(),
      agFilters: this.getTableFilters(),
      isExportPopupActive: false,
      accountId: this.$route.query['account-id'],
      account: null,

      toaster: {
        show: false,
        message: '',
        icon: require('@/assets/images/success.svg'),
        closeButtonText: 'close'
      }
    }
  },

  computed: {
    deletedCampaign () {
      return this.$store.getters['campaign/deletedCampaign']
    },
    deletedAccount () {
      return this.$store.getters['accounts/deletedAccount']
    },
    rowsCount () {
      return this.$refs['systemCampaignsTable'].getItemsCount()
    },
    showDeleteBtn () {
      if (this.deletedAccount && this.deletedAccount.id === this.account.id) {
        return false
      }

      return true
    }
  },

  watch: {
    deletedCampaign (deletedCampaign) {
      this.toaster.show = true
      this.toaster.message = `${trim(deletedCampaign.name, 20)} Campaign has been deleted.`

      setTimeout(() => {
        this.toaster.show = false
      }, 4000)

      this.$refs['systemCampaignsTable'].refreshGrid()
    },

    deletedAccount (deletedAccount) {
      this.toaster.show = true
      this.toaster.message = `${trim(deletedAccount.name, 20)}'s account has been deleted.`

      setTimeout(() => {
        this.toaster.show = false
      }, 4000)
    }
  },
  created () {
    this.getFilteredAccount()
  },
  methods: {
    async deleteAccount (accountId) {
      this.$refs['delete-account'].deleteAccount(accountId)
    },
    async getFilteredAccount () {
      if (this.$route.query['account-id']) {
        await this.$store.dispatch('accounts/getAccountById', {accountId: this.$route.query['account-id']})

        this.account = this.$store.getters['accounts/getAccount']
      }
    },
    getTableFilters () {
      const accountId = this.$route.query['account-id']

      return {
        page: 1,
        is_admin: 1,
        name: '',
        objective: null,
        type: null,
        advertisers: null,
        status: null,
        manager: null,
        deliveringStatus: null,
        accountsIds: accountId ? [accountId] : null,
        campaignTag: this.tag
      }
    },
    getTableOptions () {
      return {
        dataSource: SystemCampaignsDataSource
      }
    },
    getTableColumns () {
      return [
        {
          headerName: 'ID',
          field: 'id',
          minWidth: 100,
          valueGetter (params) {
            if (params.data) {
              return `#${params.data.id}`
            }
          }
        },
        {
          headerName: 'Campaign Name',
          field: 'name',
          headerComponentFramework: 'CampaignNameHeader',
          minWidth: 360,
          autoHeight: true,
          cellRendererFramework: 'CampaignColumn'
        },
        {
          headerName: 'Created Date',
          field: 'created_at',
          minWidth: 180,
          sortable: true,
          valueGetter (params) {
            if (params.data && params.data.created_at) {
              return getDateAndTimeFromDateTimeString(params.data.created_at)
            }

            return 'N/A'
          },
          cellRendererFramework: 'CreatedAtColumn'
        },
        {
          headerName: 'Status',
          field: 'status',
          minWidth: 150,
          autoHeight: true,
          cellRendererFramework: 'CampaignStatus'
        },
        {
          headerName: 'Advertising Channel',
          field: 'advertising_channel',
          minWidth: 210,
          valueGetter (params) {
            if (params.data) {
              return params.data.advertising_channel ? params.data.advertising_channel.title : 'N/A'
            }
          }
        },
        {
          headerName: 'Type',
          field: 'type',
          minWidth: 200,
          valueGetter (params) {
            if (params.data) {
              return params.data.display_type ? params.data.display_type.display_name : 'N/A'
            }
          }
        },
        {
          headerName: 'Objective',
          field: 'objective',
          minWidth: 160,
          valueGetter (params) {
            if (params.data) {
              return params.data.objective ? params.data.objective.display_name : 'N/A'
            }
          }
        },
        {
          headerName: 'Start/End Date',
          field: 'start_date',
          minWidth: 300,
          valueGetter (params) {
            if (params.data) {
              const startDate = !params.data.start_date ? 'Once it\'s approved' : params.data.start_date
              const endDate = !params.data.end_date ? 'No End Date' : params.data.end_date

              return `${startDate} / ${endDate}`
            }
          }
        },
        {
          headerName: 'Spent',
          field: 'spent',
          minWidth: 110,
          cellRendererFramework: 'Spent'
        },
        {
          headerName: 'Account Manager',
          minWidth: 195,
          valueGetter (params) {
            if (params.data) {
              return params.data.account && params.data.account.account_manager ? params.data.account.account_manager.name : 'N/A'
            }
          }
        },
        {
          headerName: 'Account',
          minWidth: 200,
          valueGetter (params) {
            if (params.data) {
              return params.data.account ? params.data.account.name : 'N/A'
            }
          }
        },
        {
          headerName: 'Actions',
          field: 'actions',
          minWidth: 205,
          cellRendererFramework: 'CampaignActionsMenu',
          resizable: false
        }
      ]
    },

    applyFilters (filters) {
      this.agFilters.page = 1
      this.agFilters.is_admin = 1
      this.agFilters.name = filters.name
      this.agFilters.objective = filters.objective ? [filters.objective] : null
      this.agFilters.type = filters.type ? [filters.type] : null
      this.agFilters.advertisers = filters.advertiser ? filters.advertiser : null
      this.agFilters.status = filters.status ? filters.status : null
      this.agFilters.deliveringStatus = filters.deliveringStatus ? filters.deliveringStatus : null
      this.agFilters.manager = filters.manager ? filters.manager : null
      this.agFilters.accountsIds = filters.accountId ? [filters.accountId] : null
      this.agFilters.campaignTag = filters.campaignTag ? filters.campaignTag : null

      this.$refs['systemCampaignsTable'].applyFilters()
    }
  }
}
</script>

<style scoped lang="scss">
$light-black: #2c2c2c;
$red: #db2828;

.campaigns-owner {
  font-size: 18px;
  font-weight: normal;
  color: $light-black;
}

/deep/ .ag-cell-wrapper {
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}

.delete-account {
  height: 23px;
  left: 25.36%;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 36px;
  color: $red;
}
</style>
